"use client";

import { removeNativePreference } from "@medly/auth-config";
import { useEffect } from "react";

/**
 * When running inside the Capacitor native shell, configure the
 * status bar to overlay the webview and add a CSS class so we
 * can apply safe-area padding from CSS (theme-aware).
 *
 * IMPORTANT: This component must be placed AFTER {children} in the
 * root layout so its useEffect fires after all page content has
 * mounted. This ensures the loading overlay injected by the native
 * WKUserScript is only removed once the page is fully rendered.
 */
export function CapacitorSafeArea() {
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cap = (window as unknown as Record<string, any>).Capacitor;
    if (!cap?.isNativePlatform?.()) return;

    const platform = cap?.getPlatform?.();
    const pickerUrl =
      platform === "android" ? "http://localhost" : "capacitor://localhost";

    document.documentElement.classList.add("capacitor-native");

    import("@capacitor/status-bar").then(({ StatusBar, Style }) => {
      StatusBar.setOverlaysWebView({ overlay: true });
      StatusBar.setStyle({ style: Style.Light });
    });

    // Dev helper: call window.resetMedlyPlatform() from Safari Web Inspector
    // to clear the platform preference and return to the picker.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).resetMedlyPlatform = async () => {
      await removeNativePreference("selectedPlatform");
      window.location.replace(pickerUrl);
    };

    // Remove the loading overlay injected by the native WKUserScript.
    // requestIdleCallback fires when the browser is genuinely idle —
    // all pending renders, re-renders, and paints are complete.
    // This handles cascading re-renders (responsive breakpoints,
    // auth state, data loading) that double-rAF misses.
    const remove = () => document.getElementById("__cap_loading")?.remove();
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(remove);
    } else {
      requestAnimationFrame(() => requestAnimationFrame(remove));
    }
  }, []);

  return null;
}
